/*
 * transparent-video.js — composites a stacked-matte video into real
 * per-pixel transparency, in any browser.
 *
 * The source video is double height: picture on the top half, its alpha mask
 * as greyscale on the bottom half. A WebGL shader samples both per frame and
 * writes premultiplied RGBA to a canvas, so the result composites over the
 * page exactly like a transparent PNG would.
 *
 * This exists because the two native routes each cover only half the market
 * (WebM/VP9 alpha excludes Safari; HEVC alpha excludes everything else and
 * can only be encoded on macOS). The transport here is ordinary H.264, so
 * decoding is universal and the transparency is done by us.
 *
 * Falls back to the poster image whenever WebGL or video playback is
 * unavailable, so the worst case is a still frame, never a broken box.
 */
(function () {
  'use strict';

  var VERT = [
    'attribute vec2 a_pos;',
    'varying vec2 v_uv;',
    'void main() {',
    '  v_uv = (a_pos + 1.0) * 0.5;',
    '  gl_Position = vec4(a_pos, 0.0, 1.0);',
    '}'
  ].join('\n');

  // UNPACK_FLIP_Y_WEBGL is on, so v = 0 is the BOTTOM of the video (the
  // matte) and v = 1 is the top (the picture).
  var FRAG = [
    'precision mediump float;',
    'uniform sampler2D u_tex;',
    'varying vec2 v_uv;',
    'void main() {',
    '  vec2 colorUV = vec2(v_uv.x, v_uv.y * 0.5 + 0.5);',
    '  vec2 alphaUV = vec2(v_uv.x, v_uv.y * 0.5);',
    '  vec3 rgb = texture2D(u_tex, colorUV).rgb;',
    '  float a = texture2D(u_tex, alphaUV).r;',
    '  gl_FragColor = vec4(rgb * a, a);',   // premultiplied, matches the canvas
    '}'
  ].join('\n');

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { gl.deleteShader(s); return null; }
    return s;
  }

  function setup(root) {
    if (root.__tvInit) return;
    root.__tvInit = true;

    var video = root.querySelector('.transparent-video__source');
    var canvas = root.querySelector('.transparent-video__canvas');
    if (!video || !canvas) return;

    var fail = function () { root.setAttribute('data-tv-fallback', ''); };

    var gl = null;
    try {
      gl = canvas.getContext('webgl', { premultipliedAlpha: true, alpha: true, antialias: false })
        || canvas.getContext('experimental-webgl', { premultipliedAlpha: true, alpha: true, antialias: false });
    } catch (e) { gl = null; }
    if (!gl) { fail(); return; }

    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) { fail(); return; }

    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { fail(); return; }
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.clearColor(0, 0, 0, 0);

    var sized = false;
    function size() {
      if (!video.videoWidth) return false;
      // The video is double height; the visible frame is half of it.
      var w = video.videoWidth;
      var h = video.videoHeight / 2;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
      return true;
    }

    var running = false;
    var frame = null;
    var observer = null;
    var inRange = !('IntersectionObserver' in window);
    var disposed = false;
    function canRun() { return !disposed && inRange && !document.hidden && root.isConnected && !root.closest('[data-motion-paused]'); }
    function draw() {
      frame = null;
      if (!running || !canRun()) { stop(); return; }
      if (sized || (sized = size())) {
        if (video.readyState >= 2) {
          try {
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            root.setAttribute('data-tv-ready', '');
          } catch (e) { /* texture not ready this frame */ }
        }
      }
      frame = requestAnimationFrame(draw);
    }

    function start() {
      if (!running && canRun()) { running = true; frame = requestAnimationFrame(draw); }
    }
    function stop() {
      running = false;
      if (frame !== null) { cancelAnimationFrame(frame); frame = null; }
    }
    function syncPlayback() {
      if (canRun()) { video.play().catch(function () {}); start(); }
      else { video.pause(); stop(); }
    }
    function onLoadedData() { sized = size(); syncPlayback(); }
    video.addEventListener('loadeddata', onLoadedData);
    video.addEventListener('error', fail);
    document.addEventListener('visibilitychange', syncPlayback);

    // Loaded media must not restart a renderer outside its visibility window.
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { inRange = entry.isIntersecting; });
        syncPlayback();
      }, { rootMargin: '200px' });
      observer.observe(root);
    }
    if (video.readyState >= 2) sized = size();
    syncPlayback();

    root.__tvDispose = function () {
      disposed = true;
      syncPlayback();
      if (observer) observer.disconnect();
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('error', fail);
      document.removeEventListener('visibilitychange', syncPlayback);
      root.__tvInit = false;
      delete root.__tvDispose;
    };
  }

  function init() {
    var nodes = document.querySelectorAll('[data-transparent-video]');
    for (var i = 0; i < nodes.length; i++) setup(nodes[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function (event) {
    var nodes = event.target.querySelectorAll('[data-transparent-video]');
    for (var i = 0; i < nodes.length; i++) if (nodes[i].__tvDispose) nodes[i].__tvDispose();
  });
})();
